import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { divisions } from "@/lib/config";
import { RevealText, Reveal, RevealLines } from "@/lib/motion";

export default function DivisionPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const d = divisions.find((x) => x.id === id);
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);

  if (!d) {
    return (
      <div className="pt-40 px-8 max-w-3xl mx-auto">
        <div className="font-display text-5xl">Divisione non trovata</div>
        <button onClick={() => nav("/")} className="btn-ghost mt-8"><ArrowLeft size={14} /> Torna alla home</button>
      </div>
    );
  }

  const next = divisions[(divisions.findIndex((x) => x.id === id) + 1) % divisions.length];

  return (
    <div ref={ref}>
      {/* Hero */}
      <section className="relative h-[85vh] min-h-[600px] overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={d.hero} alt={d.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/30" />
        </motion.div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 h-full flex flex-col justify-end pb-20">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="font-mono-tech text-white/60 hover:text-[#ff4b00]"><ArrowLeft size={12} className="inline mr-1" />Torna alla home</Link>
            </div>
            <div className="num-chip mb-6"><span className="dot" /> Capitolo {d.number} · {d.tag}</div>
          </Reveal>
          <RevealText text={d.title} className="font-display text-white text-[14vw] md:text-[9vw] leading-[0.9]" />
          <Reveal delay={0.2}><div className="font-editorial text-white/70 text-3xl md:text-5xl mt-3">— {d.subtitle}</div></Reveal>
        </div>
      </section>

      {/* Body */}
      <section className="py-24 md:py-32 border-b border-[#141414]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <div className="font-mono-tech text-white/50 mb-4">◈ Che cosa facciamo</div>
              <p className="font-editorial text-3xl md:text-4xl leading-[1.1] text-white">{d.lead}</p>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:pl-8 md:border-l md:border-[#1a1a1a]">
            <Reveal delay={0.1}>
              <div className="font-mono-tech text-white/50 mb-6">◇ Servizi</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {d.bullets.map((b) => (
                  <li key={b} className="border border-[#1a1a1a] p-6 flex items-start gap-3">
                    <Check size={16} className="text-[#ff4b00] mt-1 shrink-0" />
                    <span className="text-white/85">{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-24 md:py-32 border-b border-[#141414]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex items-baseline justify-between mb-12">
              <div>
                <div className="font-mono-tech text-white/50 mb-4">◐ Cantieri & lavori</div>
                <div className="font-display text-white text-4xl md:text-6xl">Portfolio {d.title}</div>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {d.gallery.map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="photo-frame aspect-[4/5] group"
              >
                <img src={src} alt={`${d.title} #${i + 1}`} className="transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Next */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <RevealLines as="div" className="font-display text-white text-[8vw] md:text-[5vw] leading-[0.95]"
            lines={["Prossimo capitolo →"]} />
          <Link to={`/divisione/${next.id}`} className="mt-10 inline-flex items-baseline gap-6 group">
            <span className="font-mono-tech text-white/40">{next.number}</span>
            <span className="font-display text-white text-4xl md:text-7xl group-hover:text-[#ff4b00]">{next.title}</span>
            <ArrowRight size={40} className="text-[#ff4b00] group-hover:translate-x-2 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
